/**
 * Swiftaid link API.
 *
 * Two endpoints, both scoped to an organisation:
 *   POST /api/Organization/{id}/integration/swiftaid/link/start
 *       begin linking, returns the URL of the external Swiftaid form
 *   POST /api/Organization/{id}/integration/swiftaid/link/refresh
 *       re-read the current link state
 *
 * Both responses are read defensively. The API wraps entities as
 * `{ success, error, data }` in most places but the Swiftaid controller is new,
 * so the unwrapping below also accepts a bare payload and more than one field
 * name for the URL and the connected flag.
 */

import constants from '../../../constants';
import {
  SwiftaidLinkState,
  readGiftAidStatus,
  swiftaidLinkState
} from './constants';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

const getSession = () => {
  const raw = localStorage['session'];
  if (!raw || raw === 'undefined') {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    const detail = await response.text().catch(() => response.statusText);
    throw new Error(`API ${response.status}: ${detail || response.statusText}`);
  }
  // A 204 carries no body. Treat it as an empty payload rather than a parse error.
  const body = await response.text();
  if (!body) {
    return null;
  }
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

/**
 * Peel the `{ data: ... }` envelope, tolerating both the wrapped and the bare
 * form, and the double wrap (`{ data: { data: ... } }`) the legacy record
 * handlers produce.
 */
const unwrap = (payload: any): any => {
  let current = payload;
  while (current && typeof current === 'object' && 'data' in current) {
    current = current.data;
  }
  return current;
};

const readIsConnected = (payload: any): boolean => {
  const value = unwrap(payload);

  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return (
      value.toLowerCase() === 'connected' || value.toLowerCase() === 'linked'
    );
  }
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (typeof value.isConnected === 'boolean') {
    return value.isConnected;
  }
  if (typeof value.connected === 'boolean') {
    return value.connected;
  }
  if (typeof value.isLinked === 'boolean') {
    return value.isLinked;
  }
  if (typeof value.status === 'string') {
    const status = value.status.toLowerCase();
    return (
      status === 'connected' || status === 'linked' || status === 'completed'
    );
  }
  return false;
};

const readUrl = (payload: any): string | null => {
  const value = unwrap(payload);

  if (typeof value === 'string') {
    return value || null;
  }
  if (!value || typeof value !== 'object') {
    return null;
  }
  const url = value.url ?? value.link ?? value.redirectUrl ?? value.linkUrl;
  return typeof url === 'string' && url ? url : null;
};

/**
 * Begin linking.
 *
 * The response is `{ data: "<url>", success: true, error: null }`: when
 * `success` is true, `data` is the URL of the external Swiftaid form. Returns
 * that URL for the caller to navigate to, or null when the API reported a
 * failure or answered without one, which the caller has to surface rather than
 * navigating to `about:blank`.
 *
 * A 200 carrying `success: false` is a failure. Only `response.ok` is checked
 * upstream, so without this the envelope's own verdict would be ignored and the
 * browser would leave the dashboard on a request that did not succeed.
 */
export const startSwiftaidLink = async (
  organizationId: string | number
): Promise<string | null> => {
  const response = await fetch(
    `${API_BASE}/api/Organization/${organizationId}/integration/swiftaid/link/start`,
    {
      method: 'POST',
      headers: getHeaders()
    }
  );
  const payload = await handleApiResponse(response);

  if (payload && typeof payload === 'object' && payload.success === false) {
    return null;
  }
  return readUrl(payload);
};

/**
 * Re-read whether the organisation has completed the Swiftaid form.
 *
 * Prefers a `giftAidStatus` in the response, matching the field the organisation
 * record carries, and falls back to the boolean forms above so an older or
 * simpler response shape still resolves to linked or notLinked rather than to
 * `unknown`.
 */
export const refreshSwiftaidLink = async (
  organizationId: string | number
): Promise<SwiftaidLinkState> => {
  const response = await fetch(
    `${API_BASE}/api/Organization/${organizationId}/integration/swiftaid/link/refresh`,
    {
      method: 'POST',
      headers: getHeaders()
    }
  );
  const payload = await handleApiResponse(response);
  const value = unwrap(payload);

  const reported = readGiftAidStatus(
    typeof value === 'string' ? value : value?.giftAidStatus
  );
  if (reported !== swiftaidLinkState.unknown) {
    return reported;
  }
  return readIsConnected(payload)
    ? swiftaidLinkState.linked
    : swiftaidLinkState.notLinked;
};
